import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl } from "@angular/forms";
import { AccessControlService } from "app/access-control/access-control-service";
import {
  Events,
  IonicPage,
  LoadingController,
  ModalController,
  NavController,
  NavParams,
  PopoverController,
  ToastController,
} from "ionic-angular";
import { SortOptionsAllergies } from "models/content-wrapper/sort-options-allergies";
import * as moment from "moment-timezone";
import { EditViewPopoverPage } from "navigation/components/edit-view-popover/edit-view-popover";
import { UtcDateTimeZonePipe } from "pipes/output-formatters/utc-date-timezone.pipe";
import { NavBarServiceProvider } from "providers/nav-bar-service/nav-bar-service";
import {
  PatientChartIdChangeSubscription,
  subscribeToPatientChartIdChange,
} from "providers/nav-bar-service/patient-chart-id-change";
import { ReferralsServiceProvider } from "providers/patient-charts/referrals-service";
import { forkJoin } from "rxjs/observable/forkJoin";
import { of } from "rxjs/observable/of";
import { catchError, mergeMap, startWith, take } from "rxjs/operators";
import ProfessionalProvider from "../../../models/resource-management/professional-provider";
import { PatientAllergiesService } from "../../../providers/patient-charts/patient-allergies-service";
import { ResourceManagementService } from "../../../providers/resource-management/resource-management.service";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import { BasePage } from "../../base-page";
import AllergyEditPage from "../allergies/allergy-edit/allergy-edit";
import {
  AllergyHistory,
  AllergyOriginalHistory,
  AllergyResults,
  PatientAllergy,
  PatientAllergyChangeTypes,
} from "./patient-allergies";

@IonicPage({
  name: "patient-allergies",
  segment: "patient-allergies/:patientId",
})
@Component({
  selector: "patient-allergies",
  templateUrl: "allergies.html",
  providers: [UtcDateTimeZonePipe],
})
export class AllergiesPage extends BasePage implements OnInit, OnDestroy {
  sortOptions: SortOptionsAllergies = {
    column: "CreatedDatetime",
    AllergyType: "desc",
    AllergyName: "desc",
    CreatedBy: "desc",
    CreatedDatetime: "desc",
    Severity: "desc",
  };
  showAddAllergy = true;
  pageType: string = "";
  spinner = null;
  spinnerCounter = 0;
  isPalliative: boolean;
  error: boolean = false;
  allergyResults: AllergyResults = {
    patientAllergy: [],
    professionalProviders: new Map<number, ProfessionalProvider>(),
  };
  allergy: PatientAllergy;
  allergyTypes;
  showDeletedAllergies = false;
  searchField = new FormControl("");
  searchValue = "";
  // NOVA-1645 — URL :id → re-fetch subscription (see patient-chart-id-change.ts)
  private patientCtxSub: PatientChartIdChangeSubscription;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public modalCtrl: ModalController,
    public loadingCtrl: LoadingController,
    private popoverCtrl: PopoverController,
    public accessCntrl: AccessControlService,
    private allergyService: PatientAllergiesService,
    private resourceManagementService: ResourceManagementService,
    public referralsService: ReferralsServiceProvider,
    private toastCtrl: ToastController,
    state: StateManagementService,
    private utcDateTimeZonePipe: UtcDateTimeZonePipe,
    private events: Events,
    private navBarService: NavBarServiceProvider
  ) {
    super(state);

    this.referralsService.ACTIVE_TAB_NAME = "allergies";
    if (this.referralsService.PATIENT_STATUS) {
      this.pageType = this.referralsService.PATIENT_STATUS;
    }
    this.isPalliative =
      this.stateData.patientCareType &&
      this.stateData.patientCareType.toLowerCase() == "palliative";
  }

  ngOnInit() {
    window.localStorage.setItem("previousPageActive", "true");
    this.showAddAllergy = this.allergyService.showAllergyHeaderFlag;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    this.searchField.valueChanges
      .pipe(startWith(this.searchField.value))
      .subscribe((value: string) => {
        this.searchValue = value.toString().toLowerCase();
        this.allergyResults.patientAllergy = [
          ...this.allergyResults.patientAllergy,
        ];
      });

    // NOVA-1645 — Ionic reuses this page across a hash :id change. loadData()
    // is keyed on referralsService.PATIENT_ID (the internal chart id = URL id),
    // so on a URL id change sync that field and re-fetch. Set up in ngOnInit;
    // torn down in ngOnDestroy.
    if (!this.patientCtxSub) {
      this.patientCtxSub = subscribeToPatientChartIdChange(
        this.events,
        this.navBarService,
        {
          getBoundId: () => this.referralsService.PATIENT_ID,
          onChange: (urlId) => {
            this.referralsService.PATIENT_ID = urlId;
            this.loadData();
          },
        }
      );
    }
  }

  ngOnDestroy(): void {
    // NOVA-1645 — tear down the specific handler ref (Events.unsubscribe by
    // topic alone would drop other subscribers on this topic).
    if (this.patientCtxSub) {
      this.patientCtxSub.unsubscribe();
      this.patientCtxSub = undefined;
    }
  }

  ionViewWillEnter(): void {
    this.loadData();
  }

  getReactionDisplay(patientAllergy: PatientAllergy) {
    if (patientAllergy.reactionOther) {
      return `Other - ${patientAllergy.reactionOther}`;
    }
    return patientAllergy.reaction;
  }

  getDisplayName(externalId: number, date: string) {
    const dateObj = new Date(Date.parse(date));
    const professionalProvider =
      this.allergyResults.professionalProviders.get(externalId);
    if (!professionalProvider) {
      return "";
    }
    if (typeof professionalProvider === "string") {
      return professionalProvider;
    }
    return professionalProvider.findDisplayName(dateObj);
  }

  getLastModifiedDate(patientAllergys: PatientAllergy[]) {
    patientAllergys.forEach((allergy: PatientAllergy) => {
      const timeZoneDetails = this.getLatestDate(allergy);
      allergy.latestDate = timeZoneDetails.time;
      allergy.latestTimeZone = timeZoneDetails.timeZone;
      allergy.lastEditedBy = this.getLastEnteredBy(allergy.createdBy, allergy);
      allergy.originalHistorySelected = false;
      if (allergy.historyMetaData && allergy.historyMetaData.length > 0) {
        allergy.originalHistory = this.originalHistoryRecord(allergy);
      }
    });
    return patientAllergys;
  }

  originalHistoryRecord(
    patientAllergy: PatientAllergy
  ): AllergyOriginalHistory {
    const originalRecord: Partial<AllergyOriginalHistory> = {};
    for (const changeType of PatientAllergyChangeTypes) {
      const oldestRecord = [...patientAllergy.historyMetaData]
        .sort((a, b) => a.modifiedDatetime.localeCompare(b.modifiedDatetime))
        .find((allergy) => {
          return allergy.changesMadeTo === changeType.changesMadeTo;
        });
      originalRecord[changeType.field] = oldestRecord
        ? oldestRecord.previousValue
        : patientAllergy[changeType.field];
    }
    return originalRecord as AllergyOriginalHistory;
  }

  loadData(): void {
    this.increaseSpinnerCount("Loading allergies ...");
    this.events.publish("listAllergiesChange", "List Updated");
    this.loadAllergiesTypeList();
    // Start off by fetching the patient's allergies
    this.allergyService
      .getAllergies(this.referralsService.PATIENT_ID)
      .pipe(
        take(1),
        // Take the results; start a new set of observables
        mergeMap((allergyResults) => [
          // The first observable is the results without display names and emits immediately. The page renders with 'Loading...' as the display names.
          of({
            patientAllergy: this.getLastModifiedDate(
              allergyResults.patientAllergy
            ),
            professionalProviders: this.allergyResults.professionalProviders,
          }),
          // Fish through the results and extract all externalIds for modify and create, if used by the UI
          ...Array.from(
            new Set(
              allergyResults.patientAllergy.reduce<Array<number>>(
                (providerExternalIdList, patientAllergy) => {
                  providerExternalIdList.push(patientAllergy.createdBy);
                  providerExternalIdList.push(patientAllergy.modifiedBy);
                  providerExternalIdList.push(patientAllergy.deletedBy);
                  if (patientAllergy.historyMetaData) {
                    providerExternalIdList.push(
                      ...patientAllergy.historyMetaData.map(
                        (allergyHistory) => allergyHistory.modifiedBy
                      )
                    );
                  }
                  return providerExternalIdList;
                },
                []
              )
            )
          )
            // Filter undefined and when a provider has been queried previously
            .filter(
              (providerExternalId) =>
                providerExternalId &&
                !this.allergyResults.professionalProviders.has(
                  providerExternalId
                )
            )
            // Map external Id to a new observable (request)
            .map((providerExternalId) => {
              return this.resourceManagementService
                .getProviderByExternalId(providerExternalId)
                .pipe(
                  take(1),
                  catchError(() => of(`${providerExternalId}`)),
                  startWith("Loading..."),
                  mergeMap((professionalProvider) =>
                    of({
                      patientAllergy: allergyResults.patientAllergy,
                      // Add provider info to the results and emit
                      professionalProviders: new Map([
                        ...Array.from(
                          this.allergyResults.professionalProviders.entries()
                        ),
                        [providerExternalId, professionalProvider],
                      ]),
                    })
                  )
                );
            }),
        ])
      )
      .mergeAll() // Above is an observable with inner observables. Merge.
      .subscribe(
        (x: AllergyResults | any) => {
          this.allergyResults = x;
          this.sortAllergy(
            this.allergyResults,
            this.sortOptions.column,
            this.sortOptions[this.sortOptions.column]
          );
          this.decreaseSpinnerCount();
        },
        (_err) => {
          this.decreaseSpinnerCount();
          let toast = this.toastCtrl.create({
            message: "Error loading allergies",
            duration: 3000,
            position: "below",
            cssClass: "error",
          });
          toast.onDidDismiss(() => {
            // Do nothing
          });
          toast.present();
        }
      );
  }

  onPageChange() {
    this.loadData();
  }

  increaseSpinnerCount(message) {
    this.spinnerCounter++;
    if (this.spinnerCounter && this.spinner == null) {
      this.spinner = this.loadingCtrl.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  decreaseSpinnerCount() {
    if (this.spinnerCounter == 0) {
      return;
    }
    this.spinnerCounter--;
    if (this.spinnerCounter == 0) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }

  /**
   * Restore referralsService.PATIENT_ID when a sibling page has nulled it.
   *
   * PATIENT_ID is a shared, mutable field on the singleton ReferralsService.
   * The referral-tabs shell sets it to null on leave (referral-tabs.ts), so a
   * user who lands on this page from that shell can have a null PATIENT_ID by
   * the time they act. The add / edit / remove flows then call
   * `/patient-charts/null` and `/allergies/patient/null`, which the API rejects
   * with 400 — the Add modal silently never opens (its getPatientById forkJoin
   * errors) and Save/Remove fail. Re-derive the id from the route the page was
   * opened with (the `:patientId` segment, mirrored into sessionStorage).
   */
  private ensurePatientId(): void {
    if (this.referralsService.PATIENT_ID) {
      return;
    }
    const fromRoute = this.navParams.get("patientId");
    const fromStorage =
      typeof window !== "undefined" && window.sessionStorage
        ? window.sessionStorage.getItem("patientChartId")
        : null;
    const id = fromRoute || fromStorage;
    if (id) {
      this.referralsService.PATIENT_ID = id;
    }
  }

  addAllergy(): void {
    this.ensurePatientId();
    let obs = [];
    obs.push(
      this.referralsService.getPatientById(
        this.referralsService.PATIENT_ID,
        false
      )
    );
    this.increaseSpinnerCount("Creating Allergy");
    forkJoin(obs).subscribe(
      ([_x, _y, _z]: [any, any, any]) => {
        this.decreaseSpinnerCount();
        let popover = this.modalCtrl.create(
          AllergyEditPage,
          {
            patientChartId: this.referralsService.PATIENT_ID,
            allergiesList: this.allergyResults.patientAllergy,
          },
          { cssClass: "medication-add" }
        );
        popover.present({});
        popover.onDidDismiss((_mode) => {
          this.loadData();
        });
      },
      (_err) => {
        this.decreaseSpinnerCount();
        console.error("Error loading resources");
      }
    );
  }

  sortAllergy(
    allergyResults: AllergyResults,
    sortColumn: string,
    sortOrder: string
  ) {
    const getTimeSorter =
      (key: keyof PatientAllergy) => (i: PatientAllergy, j: PatientAllergy) =>
        (sortOrder === "asc" ? -1 : 1) *
        (new Date(j[key] as string).getTime() -
          new Date(i[key] as string).getTime());

    const getStringSorter = (
      keyOrCallback:
        | keyof PatientAllergy
        | ((patientAllergy: PatientAllergy) => string)
    ) => {
      if (typeof keyOrCallback === "function") {
        return (i: PatientAllergy, j: PatientAllergy) =>
          (sortOrder === "asc" ? -1 : 1) *
          keyOrCallback(i).localeCompare(keyOrCallback(j));
      }
      const key: string = keyOrCallback;
      return (i: PatientAllergy, j: PatientAllergy) => {
        const str: string = i[key] ? i[key] : "";
        return (
          (sortOrder === "asc" ? -1 : 1) * str.localeCompare(j[key] as string)
        );
      };
    };

    switch (sortColumn) {
      case "CreatedDatetime":
        allergyResults.patientAllergy.sort(getTimeSorter("latestDate"));
        break;
      case "AllergyName":
        allergyResults.patientAllergy.sort(getStringSorter("allergyName"));
        break;
      case "AllergyType":
        allergyResults.patientAllergy.sort(getStringSorter("allergyType"));
        break;
      case "Severity":
        allergyResults.patientAllergy.sort(getStringSorter("severity"));
        break;
      case "CreatedBy":
        allergyResults.patientAllergy.sort(
          getStringSorter((patientAllergy: PatientAllergy) =>
            this.getDisplayName(
              patientAllergy.lastEditedBy,
              patientAllergy.latestDate
            )
          )
        );
        break;
      default:
        break;
    }
  }

  sortBy(selectedColumn: string) {
    if (selectedColumn === "AllergyType") {
      if (this.sortOptions.column === selectedColumn) {
        this.sortOptions.AllergyType =
          this.sortOptions.AllergyType === "desc" ? "asc" : "desc";
      } else {
        this.sortOptions.column = selectedColumn;
        this.sortOptions.AllergyType = "desc";
      }
    } else if (selectedColumn === "AllergyName") {
      if (this.sortOptions.column === selectedColumn) {
        this.sortOptions.AllergyName =
          this.sortOptions.AllergyName === "desc" ? "asc" : "desc";
      } else {
        this.sortOptions.column = selectedColumn;
        this.sortOptions.AllergyName = "desc";
      }
    } else if (selectedColumn === "Severity") {
      if (this.sortOptions.column === selectedColumn) {
        this.sortOptions.Severity =
          this.sortOptions.Severity === "desc" ? "asc" : "desc";
      } else {
        this.sortOptions.column = selectedColumn;
        this.sortOptions.Severity = "desc";
      }
    } else if (selectedColumn === "CreatedBy") {
      if (this.sortOptions.column === selectedColumn) {
        this.sortOptions.CreatedBy =
          this.sortOptions.CreatedBy === "desc" ? "asc" : "desc";
      } else {
        this.sortOptions.column = selectedColumn;
        this.sortOptions.CreatedBy = "desc";
      }
    } else if (selectedColumn === "CreatedDatetime") {
      if (this.sortOptions.column === selectedColumn) {
        this.sortOptions.CreatedDatetime =
          this.sortOptions.CreatedDatetime === "desc" ? "asc" : "desc";
      } else {
        this.sortOptions.column = selectedColumn;
        this.sortOptions.CreatedDatetime = "desc";
      }
    }
    this.loadData();
  }

  onToggleDeletedAllergies(): void {
    this.showDeletedAllergies = !this.showDeletedAllergies;
    this.allergyResults.patientAllergy = [
      ...this.allergyResults.patientAllergy,
    ];
  }

  refresh(): void {
    this.loadData();
  }

  onArrowClicked(allergy: PatientAllergy): void {
    this.allergyResults.patientAllergy = this.allergyResults.patientAllergy.map(
      (x) => {
        if (x === allergy) {
          x.selected = !x.selected;
        } else {
          x.selected = false;
        }
        return x;
      }
    );
  }

  loadAllergiesTypeList() {
    this.allergyService
      .getAllergiesAndDependentList("AllergiesType")
      .subscribe((response) => {
        this.allergyService.setAllergyTypeList(response);
      });
  }

  presentEditViewPopover($event, allergy: PatientAllergy) {
    let popover = this.popoverCtrl.create(
      EditViewPopoverPage,
      {
        showButtonsAsList: true,
        hideHistory: true,
        hideAdd: true,
        isPatientChart: true,
        deleteLabel: "Remove",
      },
      {
        cssClass: "more-options-list-popover",
      }
    );
    popover.present({
      ev: $event,
    });
    popover.onDidDismiss((mode) => {
      if (mode && mode.delete) {
        this.deleteAllergy(allergy);
      } else if (mode && mode.edit) {
        this.editAllergy(allergy);
      }
    });
  }

  getTimeZone() {
    let timeZone = moment.tz.guess();
    let time = new Date();
    return moment(time).tz(timeZone).format("z");
  }

  editAllergy(allergy: PatientAllergy) {
    this.ensurePatientId();
    let popover = this.modalCtrl.create(
      AllergyEditPage,
      {
        patientChartId: this.referralsService.PATIENT_ID,
        isEdit: true,
        isDelete: false,
        allegeryItem: allergy,
        allergiesList: this.allergyResults.patientAllergy,
      },
      { cssClass: "medication-add" }
    );
    popover.present({});
    popover.onDidDismiss((_mode) => {
      this.loadData();
    });
  }

  deleteAllergy(allergy: PatientAllergy) {
    this.ensurePatientId();
    let popover = this.modalCtrl.create(
      AllergyEditPage,
      {
        patientChartId: this.referralsService.PATIENT_ID,
        isEdit: false,
        isDelete: true,
        allegeryItem: allergy,
      },
      { cssClass: "medication-add" }
    );
    popover.present({});
    popover.onDidDismiss((_mode) => {
      this.loadData();
    });
  }

  openPopOver(_page: string, _arg1: boolean) {
    throw new Error("Method not implemented.");
  }

  changePage() {
    throw new Error("Method not implemented.");
  }

  getLatestDate(item: PatientAllergy) {
    let object = { time: item.createdDatetime, timeZone: item.createdTimezone };
    if (item.historyMetaData && item.historyMetaData.length > 0) {
      object.time = item.historyMetaData[0].modifiedDatetime;
      object.timeZone = item.historyMetaData[0].modifiedTimezone;
    }
    return object;
  }

  getLastEnteredBy(lastEnteredBy, item) {
    if (item.historyMetaData && item.historyMetaData.length > 0) {
      return item.historyMetaData[0].modifiedBy;
    }
    return lastEnteredBy;
  }

  sortAllergiesHistory(allergyHistory: AllergyHistory[]) {
    allergyHistory.sort((i, j) => {
      return (
        new Date(i.modifiedDatetime).getTime() -
        new Date(j.modifiedDatetime).getTime()
      );
    });
    return allergyHistory;
  }

  shouldShowAllergy = (allergy: PatientAllergy): boolean =>
    (!allergy.isDeleted || this.showDeletedAllergies) &&
    this.allergyNotFiltered(allergy);

  allergyNotFiltered = (allergy: PatientAllergy) => {
    return Object.entries({
      allergyType: allergy.allergyType,
      allergyName: allergy.allergyName,
      reaction: allergy.reaction,
      reactionOther: allergy.reactionOther,
      severity: allergy.severity,
      createdName: this.getDisplayName(
        allergy.lastEditedBy,
        allergy.createdDatetime
      ),
      createdDatetime: this.utcDateTimeZonePipe.transform(
        allergy.latestDate,
        allergy.latestTimeZone
      ),
    }).some(
      ([_, value]) =>
        value &&
        (this.searchValue == "" ||
          value.toString().toLowerCase().includes(this.searchValue))
    );
  };
}



// WEBPACK FOOTER //
// ./src/pages/patient-chart/allergies/allergies.ts